import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { SwmsType } from '../lib/types';
import { getMe, getSwmsCatalogue, type MeResponse } from '../lib/api';

// ---------------------------------------------------------------------------
// Server state (async atoms — Suspense boundary lives in _layout)
// ---------------------------------------------------------------------------

/** Who am I, and am I entitled? Drives the request-access state. */
export const meAtom = atom<Promise<MeResponse>>(async () => getMe());

/**
 * The SWMS checklist per library — live from the Safety Library when the
 * ShareName/Shareable columns are populated, bundled otherwise. Never empty.
 */
export const swmsCatalogueAtom = atom(async () => {
  const { catalogue } = await getSwmsCatalogue();
  return {
    Building: catalogue.Building.map((d) => d.name),
    Painting: catalogue.Painting.map((d) => d.name),
  } satisfies Record<SwmsType, string[]>;
});

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

export const swmsTypeAtom = atom<SwmsType>('Painting');
export const jobCodeAtom = atom<string>('');
export const selectedSwmsAtom = atom<string[]>([]);
export const subbieEmailAtom = atom<string>('');
export const isSendingAtom = atom<boolean>(false);

/** The open work order chosen on the chips (null = none / skipped). */
export const selectedWorkOrderAtom = atom<{ id: string; name: string; detail: string } | null>(null);

/**
 * Recently used subbie addresses, per device (parity with the retired mobile
 * app's autofill). Newest first, capped — see rememberRecipientsAtom.
 */
export const recentSubbiesAtom = atomWithStorage<string[]>('recentSubbieEmails', []);
const MAX_RECENT_SUBBIES = 8;

/** Merge just-used addresses into the recent list (dedupe, newest first). */
export const rememberRecipientsAtom = atom(null, (get, set, used: string[]) => {
  const current = get(recentSubbiesAtom);
  const merged = [...used, ...current.filter((a) => !used.includes(a))];
  set(recentSubbiesAtom, merged.slice(0, MAX_RECENT_SUBBIES));
});

/** Clear the form after a successful send, keeping the chosen library. */
export const resetFormAtom = atom(null, (_get, set) => {
  set(jobCodeAtom, '');
  set(selectedSwmsAtom, []);
  set(subbieEmailAtom, '');
  set(selectedWorkOrderAtom, null);
  set(isSendingAtom, false);
});
